import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Stack,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { supabase } from "../../utils/supabaseClient";

// --- CONSTANTS ---
const PLAN_MONTHLY = "monthly";
const PLAN_ANNUAL = "annual";
const PRICE_MONTHLY = "$4.99";
const PRICE_ANNUAL = "$49.99";
const PRICE_ANNUAL_PERIOD = "/ year";
const PRICE_MONTHLY_PERIOD = "/ month";
const ANNUAL_SAVING_TEXT = "Save 17%";

const SubscriptionLock = ({ open, user, onLogout }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [selectedPlan, setSelectedPlan] = useState(PLAN_MONTHLY);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Helper to get a display name
  const getDisplayName = () => {
    if (user?.user_metadata?.full_name) {
      return user.user_metadata.full_name.split(" ")[0]; // First name from Google
    }
    // For email users, we might not have a name, so we use a friendly default
    return "Traveler";
  };

  const handlePayment = async () => {
    setIsLoading(true);
    setError("");

    try {
      console.log(
        `[SubscriptionLock] Creating checkout for ${user.email} on plan: ${selectedPlan}`
      );

      const { data, error } = await supabase.functions.invoke(
        "create-stripe-checkout",
        {
          body: JSON.stringify({
            email: user.email,
            plan: selectedPlan,
            theme: localStorage.getItem("themeMode") || "dark",
          }),
        }
      );

      if (error) throw error;

      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("No checkout URL returned.");
      }
    } catch (err) {
      console.error("Payment Error:", err);
      setError("Could not initiate payment. Please contact support.");
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      // 🔒 Prevent closing: Must pay or logout
      onClose={() => {}}
      aria-labelledby="subscription-lock-title"
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          width: { xs: "95%", sm: "800px" },
          maxWidth: "800px",
          maxHeight: "90vh",
          overflowY: "auto",
          bgcolor: "background.paper",
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          boxShadow: 24,
          p: { xs: 2, md: 3 },
          position: "relative",
          outline: "none",
        }}
      >
        {/* --- Header / Logo --- */}
        <Box sx={{ width: "100%", textAlign: "center", pt: 1, pb: 2 }}>
          <img
            src={
              theme.palette.mode === "dark" ? "/pro-dark.svg" : "/pro-light.svg"
            }
            alt="FlyBnB Pro"
            style={{ width: "180px", height: "auto" }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            width: "100%",
          }}
        >
          {/* --- LEFT COLUMN: Benefits --- */}
          <Box
            sx={{
              width: { xs: "100%", md: "45%" },
              pr: { xs: 0, md: 3 },
              mb: { xs: 3, md: 0 },
              borderRight: {
                xs: "none",
                md: `1px solid ${theme.palette.divider}`,
              },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              gutterBottom
              sx={{ textAlign: { xs: "center", md: "left" } }}
            >
              Complete Your Account
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Typography variant="body1">🚀 Unlimited Searches</Typography>
              <Typography variant="body1">
                📊 AirBnB Review AI Analyzer
              </Typography>
              <Typography variant="body1">🎯 Advanced Filters</Typography>
              <Typography variant="body1">🌍 Multi-currency Support</Typography>
              <Typography variant="body1">🛠️ Trip Helper AI</Typography>
            </Stack>
          </Box>

          {/* --- RIGHT COLUMN: Plan Selection --- */}
          <Box
            sx={{
              width: { xs: "100%", md: "55%" },
              pl: { xs: 0, md: 3 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                Welcome, {getDisplayName()}! 👋
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 1, color: "primary.main", fontWeight: "medium" }}
              >
                Select a plan to activate your membership.
              </Typography>
            </Box>

            {/* Plan Boxes */}
            <Stack direction="row" spacing={2} sx={{ width: "100%", mb: 3 }}>
              {/* Monthly */}
              <Box
                onClick={() => !isLoading && setSelectedPlan(PLAN_MONTHLY)}
                sx={{
                  flex: 1,
                  p: 1.5,
                  textAlign: "center",
                  borderRadius: 2,
                  border: `2px solid ${selectedPlan === PLAN_MONTHLY ? theme.palette.primary.main : theme.palette.divider}`,
                  bgcolor:
                    selectedPlan === PLAN_MONTHLY
                      ? theme.palette.action.selected
                      : "transparent",
                  cursor: isLoading ? "default" : "pointer",
                  transition: "all 0.2s",
                }}
              >
                <Typography variant="body1" fontWeight="bold">
                  Monthly
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {PRICE_MONTHLY}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {PRICE_MONTHLY_PERIOD}
                </Typography>
              </Box>

              {/* Annual */}
              <Box
                onClick={() => !isLoading && setSelectedPlan(PLAN_ANNUAL)}
                sx={{
                  flex: 1,
                  p: 1.5,
                  textAlign: "center",
                  borderRadius: 2,
                  border: `2px solid ${selectedPlan === PLAN_ANNUAL ? theme.palette.primary.main : theme.palette.divider}`,
                  bgcolor:
                    selectedPlan === PLAN_ANNUAL
                      ? theme.palette.action.selected
                      : "transparent",
                  cursor: isLoading ? "default" : "pointer",
                  transition: "all 0.2s",
                  position: "relative",
                }}
              >
                <Typography variant="body1" fontWeight="bold">
                  Yearly
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {PRICE_ANNUAL}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {PRICE_ANNUAL_PERIOD}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "success.main",
                    fontWeight: "bold",
                  }}
                >
                  {ANNUAL_SAVING_TEXT}
                </Typography>
              </Box>
            </Stack>

            {/* Subscribe Button */}
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handlePayment}
              disabled={isLoading}
              sx={{
                mb: 2,
                fontWeight: "bold",
                py: 1.5,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                `Subscribe & Activate`
              )}
            </Button>

            {error && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}

            <Button
              variant="text"
              onClick={onLogout}
              disabled={isLoading}
              sx={{ color: "text.secondary", textTransform: "none" }}
            >
              Log out
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default SubscriptionLock;
